/*
 * Created on Wed Oct 11 2023 4:46:23 PM
 * Copyright (c) AVPS Technologies Inc 2023 — All Rights Reserved
 */
import { motion } from 'framer-motion';
import { Zap, Sparkles, Shield, Wifi } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      id: 'flexible',
      icon: <Zap className="w-8 h-8" />,
      title: 'Flexible Terms',
      description: 'Choose your plan. Choose pay-as-you-go access—hourly, daily, weekly, or monthly.',
      gradient: 'from-blue-400 to-blue-600',
      // Unsplash: calendar/scheduling concept
      image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=75&fit=crop&auto=format',
    },
    {
      id: 'premium',
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Premium Setup',
      description: 'Modern, fully furnished spaces designed for productivity.',
      gradient: 'from-emerald-400 to-emerald-600',
      // Unsplash: premium modern coworking interior
      image: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=600&q=75&fit=crop&auto=format',
    },
    {
      id: 'wifi',
      icon: <Wifi className="w-8 h-8" />,
      title: 'Fast Internet',
      description: 'Fast, dependable Wi-Fi for calls, collaboration, and focused work.',
      gradient: 'from-amber-400 to-amber-600',
      // Unsplash: laptop + focused work
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=75&fit=crop&auto=format',
    },
    {
      id: 'meeting',
      icon: <Shield className="w-8 h-8" />,
      title: 'Meeting Rooms',
      description: 'Fully equipped, professional meeting rooms—book when you need them.',
      gradient: 'from-rose-400 to-rose-600',
      // Unsplash: professional meeting room
      image: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=600&q=75&fit=crop&auto=format',
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        .ft-font { font-family: var(--flex-font, 'Inter', sans-serif); }

        .ft-section {
          background: linear-gradient(to bottom right, var(--flex-secondary-dark, #0f172a), var(--flex-secondary, #1e293b), var(--flex-secondary-dark, #0f172a));
          position: relative;
          overflow: hidden;
        }
        .ft-section::before {
          content: '';
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 56px 56px;
        }
        .ft-glow {
          position: absolute; top: -120px; right: -120px;
          width: 500px; height: 500px;
          background: radial-gradient(circle, var(--flex-primary-soft, rgba(37,99,235,0.15)) 0%, transparent 70%);
          pointer-events: none;
          filter: blur(20px);
        }

        .ft-badge {
          display: inline-block;
          background: var(--flex-primary-soft, rgba(37,99,235,0.1));
          color: var(--flex-accent, #93c5fd);
          border: 1px solid var(--flex-primary-muted, rgba(147,197,253,0.18));
          padding: 0.375rem 1.125rem;
          border-radius: 9999px;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          
          margin-bottom: 1.5rem;
        }

        .ft-heading-accent {
          background: linear-gradient(135deg, var(--flex-primary, #60a5fa) 0%, var(--flex-accent, #93c5fd) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Card with image thumbnail at top */
        .ft-card {
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 1.25rem;
          overflow: hidden;
          position: relative;
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .ft-card:hover {
          background: rgba(255,255,255,0.065);
          border-color: rgba(255,255,255,0.13);
          transform: translateY(-5px);
          box-shadow: 0 24px 56px rgba(0,0,0,0.28);
        }

        .ft-card-img {
          width: 100%;
          height: 160px;
          object-fit: cover;
          display: block;
          filter: brightness(0.75) saturate(0.9);
          transition: filter 0.3s, transform 0.3s;
        }
        .ft-card:hover .ft-card-img {
          filter: brightness(0.85) saturate(1.05);
          transform: scale(1.04);
        }
        .ft-card-img-wrap {
          overflow: hidden;
          position: relative;
          flex-shrink: 0;
        }
        /* gradient over image */
        .ft-card-img-wrap::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, transparent 40%, rgba(15,23,42,0.55) 100%);
        }

        .ft-card-body {
          padding: 1.5rem 1.75rem 1.75rem;
          display: flex;
          flex-direction: column;
          flex: 1;
          position: relative;
        }

        .ft-card-border {
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          opacity: 0;
          transition: opacity 0.3s;
        }
        .ft-card:hover .ft-card-border { opacity: 1; }

        .ft-icon-wrap {
          width: 52px; height: 52px;
          border-radius: 0.875rem;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 1rem;
          flex-shrink: 0;
          transition: transform 0.3s;
        }
        .ft-card:hover .ft-icon-wrap { transform: scale(1.08) rotate(2deg); }

        .ft-rule {
          width: 28px; height: 2px;
          border-radius: 2px;
          margin-bottom: 0.625rem;
        }
      `}</style>

      <section
        id="features"
        className="ft-section ft-font py-20 sm:py-24 lg:py-32"
      >
        <div className="ft-glow" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="ft-badge">Why Choose CoworkFlex</span>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                Simple. Flexible.{' '}
                <span className="ft-heading-accent">
                  Built for How You Work.
                </span>
              </h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
                Everything you need to work, meet, and collaborate—without the overhead of a traditional office.
              </p>
            </motion.div>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="ft-card">
                  {/* Image thumbnail */}
                  <div className="ft-card-img-wrap">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="ft-card-img"
                    />
                  </div>

                  <div className="ft-card-body">
                    <div
                      className="ft-card-border"
                      style={{ background: `linear-gradient(90deg, ${feature.gradient.replace('from-','').split(' ')[0]}, transparent)` }}
                    />

                    <div
                      className={`ft-icon-wrap bg-gradient-to-r ${feature.gradient}`}
                      style={{ opacity: 0.9 }}
                    >
                      <div className="text-white">{feature.icon}</div>
                    </div>

                    <div
                      className={`ft-rule bg-gradient-to-r ${feature.gradient}`}
                    />

                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-slate-100 transition-colors duration-200">
                      {feature.title}
                    </h3>

                    <p className="text-slate-300 leading-relaxed font-medium">
                      {feature.description}
                    </p>

                    {/* hover bottom line */}
                    <div
                      className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${feature.gradient} group-hover:w-full transition-all duration-300`}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturesSection;
